// src/components/PricingPage.js
import { ArrowRight, Check, MessageSquare, X } from 'lucide-react';
import { useState } from 'react';
import { useConfig } from '../hooks/useConfig';
import Layout from './Layout';

const PricingPage = () => {
  const { site } = useConfig();
  const [selectedPlan, setSelectedPlan] = useState('plan2'); // Default to most popular

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
  };

  const handleStartTrial = (planId) => {
    // Select the plan and proceed to registration
    setSelectedPlan(planId);
    // Redirect with plan parameter
    window.location.href = `/register?plan=${planId}`;
  };

  const getSelectedPlanData = () => {
    return site.pricing.plans.find(plan => plan.id === selectedPlan);
  };

  return (
    <Layout>
      {/* Header */}
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
            Choose Your Perfect Plan
          </h1>
          <p className="mt-4 text-xl text-gray-600">
            Start with a {site.pricing.trialDays}-day free trial. No setup fees, no long-term contracts.
          </p>
          
          {/* Selected Plan Indicator */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Currently selected:</span> {getSelectedPlanData()?.name}
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-sm text-gray-600">
              💡 Click on any plan to select it, then proceed to checkout
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-8">
            {site.pricing.plans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => handlePlanSelect(plan.id)}
                className={`bg-white rounded-lg shadow-lg p-8 relative cursor-pointer transition-all duration-200 ${
                  plan.popular 
                    ? 'ring-2 ring-blue-500 transform scale-105' 
                    : selectedPlan === plan.id
                    ? 'ring-2 ring-blue-400 transform scale-102'
                    : 'hover:ring-2 hover:ring-gray-300 hover:scale-101'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                
                {selectedPlan === plan.id && !plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                      Selected
                    </span>
                  </div>
                )}
                
                <div className="text-center">
                  <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                  <p className="mt-2 text-sm text-gray-600">{plan.description}</p>
                  
                  <div className="mt-6">
                    <span className="text-xl font-bold text-blue-600">
                      Reach out for pricing
                    </span>
                  </div>
                </div>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card click when clicking button
                    handleStartTrial(plan.id);
                  }}
                  className={`w-full mt-8 px-4 py-3 rounded-lg font-medium transition-colors ${
                    selectedPlan === plan.id
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {selectedPlan === plan.id ? 'Continue with This Plan' : 'Select This Plan'}
                </button>
                
                <div className="mt-8">
                  <h4 className="font-medium text-gray-900 mb-4">What's included:</h4>
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        {feature.comingSoon ? (
                          <span className="flex-shrink-0 mt-0.5 text-xs font-medium bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                            Soon
                          </span>
                        ) : (
                          <Check className="flex-shrink-0 h-5 w-5 text-green-500 mt-0.5" />
                        )}
                        <span className={`ml-3 text-sm ${feature.comingSoon ? 'text-gray-400' : 'text-gray-700'}`}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                  
                  {plan.limitations.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-medium text-gray-900 mb-4">Not included:</h4>
                      <ul className="space-y-3">
                        {plan.limitations.map((limitation, index) => (
                          <li key={index} className="flex items-start">
                            <X className="flex-shrink-0 h-5 w-5 text-gray-400 mt-0.5" />
                            <span className="ml-3 text-sm text-gray-500">{limitation}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Feature Comparison</h2>
            <p className="mt-4 text-lg text-gray-600">
              See exactly what's included in each plan
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-4 text-lg font-medium text-gray-900">Features</th>
                  <th className="text-center py-4 text-sm font-medium text-gray-900">WhatsApp Only</th>
                  <th className="text-center py-4 text-sm font-medium text-gray-900">+ Voice</th>
                  <th className="text-center py-4 text-sm font-medium text-gray-900">+ Marketing</th>
                  <th className="text-center py-4 text-sm font-medium text-gray-900">+ Payments</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="py-4 text-sm text-gray-900">WhatsApp Booking</td>
                  <td className="text-center py-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-4 text-sm text-gray-900">Phone Call Handling</td>
                  <td className="text-center py-4"><X className="h-5 w-5 text-gray-400 mx-auto" /></td>
                  <td className="text-center py-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-4 text-sm text-gray-900">Marketing Campaigns</td>
                  <td className="text-center py-4"><X className="h-5 w-5 text-gray-400 mx-auto" /></td>
                  <td className="text-center py-4"><X className="h-5 w-5 text-gray-400 mx-auto" /></td>
                  <td className="text-center py-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-4 text-sm text-gray-900">Payment Processing</td>
                  <td className="text-center py-4"><X className="h-5 w-5 text-gray-400 mx-auto" /></td>
                  <td className="text-center py-4"><X className="h-5 w-5 text-gray-400 mx-auto" /></td>
                  <td className="text-center py-4"><X className="h-5 w-5 text-gray-400 mx-auto" /></td>
                  <td className="text-center py-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-4 text-sm text-gray-900">Monthly Bookings</td>
                  <td className="text-center py-4 text-sm text-gray-600">100</td>
                  <td className="text-center py-4 text-sm text-gray-600">300</td>
                  <td className="text-center py-4 text-sm text-gray-600">500</td>
                  <td className="text-center py-4 text-sm text-gray-600">Unlimited</td>
                </tr>
                <tr>
                  <td className="py-4 text-sm text-gray-900">Support Level</td>
                  <td className="text-center py-4 text-sm text-gray-600">Email</td>
                  <td className="text-center py-4 text-sm text-gray-600">Email + Chat</td>
                  <td className="text-center py-4 text-sm text-gray-600">Phone</td>
                  <td className="text-center py-4 text-sm text-gray-600">24/7 Priority</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Frequently Asked Questions</h2>
          </div>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                What happens during the 14-day free trial?
              </h3>
              <p className="text-gray-600">
                You get full access to your selected plan for 14 days. We'll help you set up everything, and you can start taking bookings immediately. No credit card required to start.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Can I change my plan later?
              </h3>
              <p className="text-gray-600">
                Absolutely! You can upgrade or downgrade your plan at any time. Changes take effect on your next billing cycle, and we'll help you migrate your settings.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                How quickly can I get started?
              </h3>
              <p className="text-gray-600">
                Most businesses are up and running within 5 minutes. Our setup wizard guides you through connecting your phone number and calendar, and we're here to help if needed.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                What if my customers don't like using WhatsApp?
              </h3>
              <p className="text-gray-600">
                No problem! Plans with voice calling let customers book the traditional way too. The AI handles both channels seamlessly, so customers can choose their preferred method.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Is there a setup fee or contract?
              </h3>
              <p className="text-gray-600">
                No setup fees, no contracts. You can cancel anytime with 30 days notice. We believe in earning your business every month with great service.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">
            Ready to get started?
          </h2>
          <p className="mt-4 text-xl text-blue-100">
            Be among the first to put AI to work for your bookings
          </p>
          <div className="mt-8">
            <button 
              onClick={() => handleStartTrial(selectedPlan)}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-100 flex items-center mx-auto w-fit"
            >
              Continue with {getSelectedPlanData()?.name || 'Selected Plan'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <p className="mt-4 text-blue-100">
              {site.pricing.trialDays}-day free trial • No credit card required
            </p>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-t">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <MessageSquare className="h-8 w-8 text-blue-600 mx-auto mb-4" />
              <h3 className="font-medium text-gray-900">WhatsApp Business API</h3>
              <p className="text-sm text-gray-600">Powered by Twilio's WhatsApp Business API integration</p>
            </div>
            <div>
              <div className="h-8 w-8 text-blue-600 mx-auto mb-4 bg-blue-100 rounded flex items-center justify-center">
                <span className="text-xs font-bold">G</span>
              </div>
              <h3 className="font-medium text-gray-900">Google Calendar</h3>
              <p className="text-sm text-gray-600">Real-time sync with Google Calendar via official API</p>
            </div>
            <div>
              <div className="h-8 w-8 text-blue-600 mx-auto mb-4 bg-blue-100 rounded flex items-center justify-center">
                <span className="text-xs font-bold text-blue-600">☁</span>
              </div>
              <h3 className="font-medium text-gray-900">Google Cloud</h3>
              <p className="text-sm text-gray-600">Data stored securely on Google Cloud infrastructure</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PricingPage;